import React, { useContext, useEffect, useState } from "react";
import { PatioContext } from "context/patio-calculator";
import patioData from "./data";
import Price from "../price";
import SelectField from "../../_components/select-field";
import InputField from "../../_components/input-field";
import ColorPicker from "../../_components/color-picker";
import BaseInput from "../../_components/base-input";
import FileUpload from "../../_components/file-upload";

const Sides = ({ sides, valueType }) => {
    const { customAttributes, setCustomAttributes, configuration, updateConfiguration } = useContext(PatioContext);

    const [selectedSide, setSelectedSide] = useState(
        customAttributes[`c_${`Print Side`}`] || [],
    );

    useEffect(() => {
        let updatedSides;
        if (configuration.default_variant_id && (!customAttributes[`c_${`Print Side`}`] || customAttributes[`c_${`Print Side`}`].length === 0)) {
            updatedSides = sides
                .filter((side) => side.id === configuration.default_variant_id)
                .map((side) => side.title);
        } else if (customAttributes[`c_${`Print Side`}`] && customAttributes[`c_${`Print Side`}`].length > 0) {
            updatedSides = customAttributes[`c_${`Print Side`}`];
        } else {
            updatedSides = sides.length > 0 ? [sides[0].title] : [];
        }

        setSelectedSide(updatedSides);
        setCustomAttributes({
            ...customAttributes,
            [`c_${`Print Side`}`]: updatedSides,
        });

        return () => {
            setSelectedSide([]);
            // Remove "c_Print Side"
            const updatedAttributes = { ...customAttributes };
            delete updatedAttributes[`c_${`Print Side`}`];
            setCustomAttributes(updatedAttributes);
        };
    }, [sides, configuration.default_variant_id]);

    useEffect(() => {
        const selectedSidesIds = sides
            .filter((side) => selectedSide.includes(side.title))
            .map((side) => Number(side.id));
    
        updateConfiguration({
            customization_variant_ids: [
                ...new Set(
                    configuration.customization_variant_ids
                        .filter((id) => !sides.map((side) => Number(side.id)).includes(Number(id)))
                        .concat(selectedSidesIds),
                ),
            ],
        });
    
        return () => {
            updateConfiguration({
                customization_variant_ids: configuration.customization_variant_ids.filter(
                    (id) => !sides.map((side) => Number(side.id)).includes(Number(id))
                ),
            });
        };
    }, [selectedSide]);
    
    const handleSideChange = (sideTitle, sideId) => {
        const sideIdNum = Number(sideId);
        let updatedSides;
    
        if (valueType === "Multiple") {
            if (selectedSide.includes(sideTitle)) {
                if (selectedSide.length === 1) {
                    return;
                }
                updatedSides = selectedSide.filter((side) => side !== sideTitle);
            } else {
                updatedSides = [...selectedSide, sideTitle];
            }
        } else {
            updatedSides = [sideTitle];
        }
    
        setSelectedSide(updatedSides);
        setCustomAttributes({
            ...customAttributes,
            [`c_${`Print Side`}`]: updatedSides,
        });
    
        updateConfiguration({
            customization_variant_ids: [
                ...new Set(
                    configuration.customization_variant_ids.filter(
                        (id) => Number(id) !== sideIdNum
                    ).concat([sideIdNum]),
                ),
            ],
        });
    };

    return (
        <div className="configurator-section">
            <div className="configurator-section__personalize-text">
                <BaseInput
                    required
                    label="Print Sides"
                />
            </div>
            <div className="configurator-section__print-sides">
                {sides && sides.map((side) => side?.image && (
                    <div
                        key={side?.id}
                        className="configurator-section__print-sides-item"
                        onClick={() => handleSideChange(side?.title, side?.id)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                handleSideChange(side?.title, side?.id);
                            }
                        }}
                    >
                        <img className="configurator-section__print-sides-img" src={side?.image} alt={`Print Side ${side?.value}`} />
                        <div className="configurator-section__print-sides-position">
                            <input
                                type={valueType === "Multiple" ? "checkbox" : "radio"}
                                className="configurator-section__print-sides-content-checkbox"
                                value={side?.title}
                                checked={selectedSide.includes(side?.title)}
                                onChange={(event) => handleSideChange(event.target.value, side?.id)}
                            />
                            <span className={`configurator-section__print-sides-content-custom-checkbox ${selectedSide.includes(side?.title) ? "active" : ""}`} />
                            <p className="configurator-section__print-sides-content-title">{side?.title}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const DesignPreview = ({ data }) => {
    const { color, selectedFont, text, bgColor } = data;
    const defualtText = "Text Here";

    return (
        <div className="configurator-section__inner">
            <div className="configurator-section__personalize-text">
                <BaseInput
                    label="Your Design Preview"
                />
            </div>
            <div className="configurator-section__inner-design-preview" style={{ backgroundColor: bgColor }}>
                <div
                    className="configurator-section__inner-design-preview-text"
                    style={{ color: `${color}`, fontFamily: selectedFont ?? "Arial" }}
                >
                    {text || defualtText}
                </div>
            </div>
            <div className="configurator-section__personalize-text-preview-label">
                <BaseInput
                    label="( Text Preview is for reference only, final print may differ on cover.)"
                />
            </div>
        </div>
    );
};

const Personalization = ({ data, bgColor }) => {
    const { configuration, updateConfiguration, customAttributes, setCustomAttributes } = useContext(PatioContext);

    const [selectedOption, setSelectedOption] = useState(customAttributes[`c_${`Personalize Type`}`] || "text");
    const [text, setText] = useState(customAttributes[`c_${`Text`}`] || "");
    const [checked, setChecked] = useState(!!customAttributes[`c_${`Print Side`}`] || false);
    const [selectedFont, setSelectedFont] = useState(customAttributes[`c_${`Font`}`] || "Arial");
    const [color, setColor] = useState(customAttributes[`c_${`Color`}`] || "#ffffff");
    const [file, setFile] = useState(customAttributes[`c_${`Logo`}`] || null);
    const fonts = ["Arial", "Cambria", "Green", "Courier New", "Delius Unicase", "Lucida Sans Unicode", "Verdana", "Segoe Print", "Traditional Arabic", "Times New Roman"];
    const [headerPrice, setHeaderPrice] = useState();

    const handleCheck = () => {
        setChecked((prev) => !prev);
    };

    useEffect(() => {
        if (checked) {
            setCustomAttributes((prevAttributes) => {
                let attributes = { ...prevAttributes };

                if (selectedOption === "text") {
                    attributes = {
                        ...attributes,
                        [`c_${`Personalize Type`}`]: selectedOption,
                        [`c_${`Font`}`]: selectedFont,
                        [`c_${`Color`}`]: color,
                        [`c_${`Text`}`]: text,
                    };
                } else if (selectedOption === "logo") {
                    attributes = {
                        ...attributes,
                        [`c_${`Personalize Type`}`]: selectedOption,
                        [`c_${`Logo`}`]: file,
                    };
                }

                return attributes;
            });
        }

        return () => {
            setCustomAttributes((prevAttributes) => {
                const updatedAttributes = { ...prevAttributes };

                Object.keys(updatedAttributes).forEach((key) => {
                    if (["c_Personalize Type", "c_Font", "c_Color", "c_Text", "c_Logo"].includes(key)) {
                        delete updatedAttributes[key];
                    }
                });

                return updatedAttributes;
            });
        };
    }, [checked, text, selectedOption, selectedFont, color, file]);

    const handleOptionChange = (selectedOption) => {
        setSelectedOption(selectedOption.value);
    };

    const handleFontChange = (selectedFontOption) => {
        setSelectedFont(selectedFontOption.value);
    };

    const options = Object.entries(patioData.personalizationOptions).map(([key, value]) => ({
        value: key,
        label: value,
    }));

    const fontOptions = fonts.map((font) => ({
        value: font,
        label: font,
        styles: { fontFamily: font },
    }));

    useEffect(() => {
        const selectedVariantPrices = data?.variants
            .filter((variant) => configuration.customization_variant_ids.includes(variant.id))
            .map((variant) => parseFloat(variant.price) || 0);

        const sidesPrice = selectedVariantPrices.reduce((acc, price) => acc + price, 0);

        if (checked) {
            setHeaderPrice(sidesPrice);
        }
    }, [customAttributes, configuration.customization_variant_ids, data, checked]);

    return (
        <div className="configurator-section py-4">
            <div className="configurator-section__header">
                <h3 className="configurator-section__header-title">{`3. ${data?.title}`}</h3>
                <div className="configurator-section_header-leftside">
                    {checked === true && <Price value={headerPrice} className="configurator-section_header-price" />}
                    <input type="checkbox" className="configurator-section__header-checkbox" checked={checked} onChange={handleCheck} />
                </div>
            </div>
            {checked && (
                <div className="configurator-section__inner">
                    <div className="configurator-section__personalize-info">
                        <p>We'll resize the monogram that best fits your cover size.</p>
                    </div>
                    <SelectField
                        value={options.find((option) => option.value === selectedOption)}
                        onChange={handleOptionChange}
                        options={options}
                        label="Personalize Type"
                        required
                        denySearch
                    />

                    {selectedOption === "text" && (
                        <>
                            <div className="configurator-section__personalize-text">
                                <InputField
                                    type="text"
                                    value={text}
                                    onChange={(event) => setText(event.target.value)}
                                    required
                                    label="Enter your text"
                                    placeholder="Enter your Text Here"
                                />
                            </div>

                            <div className="flex w-full justify-between">
                                <div className="configurator-section__personalize-text w-[45%]">
                                    <SelectField
                                        value={fontOptions.find((option) => option.value === selectedFont)}
                                        onChange={(selectedFontOption) => handleFontChange(selectedFontOption)}
                                        options={fontOptions}
                                        label="Select your font"
                                        styles={{
                                            singleValue: (styles) => ({
                                                ...styles,
                                                fontFamily: `${selectedFont} !important`,
                                            }),
                                            option: (styles, { data: _data }) => ({
                                                ...styles,
                                                fontFamily: `${_data?.styles?.fontFamily} !important`,
                                            }),
                                        }}
                                        required
                                    />
                                </div>
                                <div className="configurator-section__personalize-text w-1/2">
                                    <ColorPicker
                                        value={color}
                                        label="Select text color"
                                        required
                                        onChange={(newColor) => {
                                            setColor(newColor);
                                        }}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {selectedOption === "logo" && (
                        <div className="configurator-section__personalize-text">
                            <FileUpload
                                multiple={false}
                                formats={["image/jpeg", "image/png", "application/pdf", "image/gif"]}
                                mbLimit={20}
                                defaultValue={customAttributes[`c_${`Logo`}`] ? [customAttributes[`c_${`Logo`}`]] : []}
                                onChange={(files) => {
                                    const uploadedLogo = files[0];
                                    setFile(uploadedLogo);
                                    setCustomAttributes((prevAttributes) => ({
                                        ...prevAttributes,
                                        [`c_${`Logo`}`]: uploadedLogo,
                                    }));
                                }}
                                required
                            />
                        </div>
                    )}

                    <Sides sides={data.variants} valueType={data.value_type} />

                    {selectedOption === "text" && (
                        <DesignPreview
                            data={{
                                color, selectedFont, text, bgColor,
                            }}
                        />
                    )}
                </div>
            )}

        </div>
    );
};

export default Personalization;
